import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContent } from '@/mocks/articleContent';

export default function ArticlePage() {
  const location = useLocation();

  // Extract slug from pathname: e.g. /allchinabuy-spreadsheet-guide/ -> allchinabuy-spreadsheet-guide
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const article = articleContent[slug as keyof typeof articleContent];

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center px-4">
            <h1 className="text-2xl font-bold text-foreground-950">Article Not Found</h1>
            <p className="mt-2 text-foreground-600">The article you are looking for does not exist.</p>
            <a href="/" className="mt-4 inline-block text-primary-600 hover:underline">
              Back to Home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-16 bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-3">
          <nav aria-label="breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><a href="/" className="hover:text-primary-600 transition-colors">Home</a></li>
              <li><i className="ri-arrow-right-s-line text-xs" /></li>
              <li><span className="text-foreground-700">{article.title}</span></li>
            </ol>
          </nav>
        </div>
      </div>

      <main className="flex-1">
        {/* Article Header */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 max-w-4xl mx-auto">
          <span className="inline-block px-3 py-1 text-xs font-semibold bg-primary-50 text-primary-700 rounded-full mb-4">
            Guide
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              Editorial Team
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              Published: {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.readTime} min read
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line" />
              Updated: {article.updated}
            </span>
          </div>
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-20 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 md:gap-12">
            <article>
              {article.sections.map((section, idx) => (
                <div key={idx} className="mb-10">
                  <h2 id={`section-${idx}`} className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {section.h2}
                  </h2>
                  <p className="text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}

              {/* Internal Link to Home */}
              <div className="mt-12 p-6 bg-background-50 rounded-xl border border-background-200">
                <p className="text-sm text-foreground-600 mb-3">
                  Looking for more AllChinaBuy resources? Check out our comprehensive guides and comparison resources.
                </p>
                <a href="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
                  Back to Home
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </article>

            {/* Sidebar TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-background-50 rounded-xl border border-background-200 p-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">Table of Contents</h3>
                <ul className="space-y-2">
                  {article.sections.map((section, idx) => (
                    <li key={idx}>
                      <a
                        href={`#section-${idx}`}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors leading-snug"
                      >
                        {section.h2}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Articles */}
              <div className="mt-6 bg-background-50 rounded-xl border border-background-200 p-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">Related Guides</h3>
                <ul className="space-y-3">
                  {Object.entries(articleContent)
                    .filter(([key]) => key !== slug)
                    .slice(0, 5)
                    .map(([key, data]) => (
                      <li key={key}>
                        <a
                          href={`/${key}/`}
                          className="text-sm text-foreground-600 hover:text-primary-600 transition-colors leading-snug"
                        >
                          {data.title}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>

              {/* CTA Box */}
              <div className="mt-6 bg-primary-500 rounded-xl p-5 text-white">
                <h3 className="text-sm font-semibold mb-2">Ready to Buy?</h3>
                <p className="text-sm text-white/80 mb-4">
                  Visit the main AllChinaBuy website to start your shopping journey.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-primary-600 text-sm font-medium rounded-lg hover:bg-white/90 transition-colors"
                >
                  Visit Main Website
                  <i className="ri-external-link-line" />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      {/* Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "author": {
            "@type": "Organization",
            "name": "AllChinaBuy Spreadsheet Editorial Team"
          },
          "datePublished": article.date,
          "dateModified": article.updated,
          "publisher": {
            "@type": "Organization",
            "name": "AllChinaBuy Spreadsheet",
            "url": "https://allchinabuyspread.boats"
          }
        })}
      </script>
    </div>
  );
}